// contexts/StickiesContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';
import * as stickiesApi from '../../api/stickiesApi';
import { usePanelContext } from './PanelContext';
import { useNotes } from './NotesContext'; // Make sure this import is added

const StickiesContext = createContext();

export function StickiesProvider({ children, selectedNoteId }) {
  // Get note context to check parent-child relationships
  const { getSelectedNote } = useNotes();
  
  // State for tracking parent notes and loading state
  const [parentNoteId, setParentNoteId] = useState(null);
  const [lastLoadedNoteId, setLastLoadedNoteId] = useState(null);
  const [stickies, setStickies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isSwapped } = usePanelContext();
  
  
  // Determine if current note is a subnote and get its parent
  useEffect(() => {
    if (selectedNoteId) {
      const currentNote = getSelectedNote();
      if (currentNote && currentNote.parent_id) {
        // It's a subnote - set the parent ID
        setParentNoteId(currentNote.parent_id);
      } else {
        // It's a top-level note - no parent
        setParentNoteId(null);
      }
    }
  }, [selectedNoteId, getSelectedNote]);
  
  // Define effective note ID AFTER the parent ID effect
  // This determines which note's stickies to show
  const effectiveNoteId = parentNoteId || selectedNoteId;

  // Debounced sticky content update
  const debouncedStickyContent = useCallback(
    
    debounce(async (sticky) => {
      console.log('📡 debouncedStickyContent - Making API call for sticky:', sticky.id); // ADD THIS
      try {
        // Always use effectiveNoteId for API calls to ensure updates happen on the parent note
        await stickiesApi.updateSticky(effectiveNoteId, sticky.id, {
          content: sticky.content,
          title: sticky.title,  // ADD THIS LINE
          type: sticky.type,
          is_minimized: sticky.is_minimized,
          main_x: sticky.main_x,        // ADD THIS
          main_y: sticky.main_y,        // ADD THIS
          main_width: sticky.main_width,  // ADD THIS
          main_height: sticky.main_height, // ADD THIS
          zIndex: sticky.zIndex,
          tag1: sticky.tag1,  // ADD THIS
          tag2: sticky.tag2,  // ADD THIS
        });
      } catch (error) {
        console.error('Error in sticky content update:', error);
        setError('Failed to update sticky');
      }
    }, 1000),
    [effectiveNoteId]
  );

  // Fetch stickies with optimization to prevent unnecessary reloads
  const fetchStickies = useCallback(async (forceReload = false) => {
    if (!effectiveNoteId) return;
    
    // Skip loading if we already loaded stickies for this note (or its parent)
    if (!forceReload && effectiveNoteId === lastLoadedNoteId) {
      console.log('Stickies already loaded for this note/parent, skipping reload');
      return;
    }
    
    try {
      setLoading(true);
      const stickiesData = await stickiesApi.fetchStickies(effectiveNoteId);
      setStickies(stickiesData);
      setLastLoadedNoteId(effectiveNoteId);
      setError(null);
    } catch (err) {
      console.error('Error loading stickies:', err);
      setError('Failed to load stickies');
    } finally {
      setLoading(false);
    }
  }, [effectiveNoteId, lastLoadedNoteId]);

  // Add new sticky - always to the effective note ID
  const addSticky = useCallback(async (newZIndex, stickyType) => {
    const newSticky = {
      content: 'New Sticky',
      title: null,
      x: 20,
      y: 100,
      width: 150,
      height: 150,
      zIndex: newZIndex,
      note_id: effectiveNoteId, // Use effective ID to add to parent
      type: stickyType
    };

    try {
      const createdSticky = await stickiesApi.createSticky(effectiveNoteId, newSticky);
      setStickies(prev => [...prev, createdSticky]);
      return createdSticky.id;
    } catch (error) {
      console.error('Error creating sticky:', error);
      setError('Failed to create sticky');
    }
  }, [effectiveNoteId]);

  // Update sticky - always on the effective note ID
  const updateSticky = useCallback(async (id, updates) => {
   // console.log('🔍 updateSticky RECEIVED:', { id, updates });
    if (!effectiveNoteId) {
      console.warn('Attempted to update sticky without valid noteId');
      return;
    }
  
    try {
      // Determine if this is a main panel update based on properties and swap state
      const hasContentProps = ['content', 'title'].some(prop => prop in updates);

      // Determine if this is ONLY a main panel layout update
      const isMainPanelUpdate = isSwapped 
        ? ['x', 'y', 'width', 'height'].some(prop => prop in updates) && !hasContentProps
        : ['main_x', 'main_y', 'main_width', 'main_height'].some(prop => prop in updates) && !hasContentProps;
      
      setStickies(prevStickies => {
        const updatedStickies = prevStickies.map(sticky => {
          if (sticky.id !== id) return sticky;
          
          // Always update all properties that are provided, regardless of panel
          const updatedSticky = {
            ...sticky,
            // Main panel properties
            main_x: updates.main_x !== undefined ? updates.main_x : sticky.main_x,
            main_y: updates.main_y !== undefined ? updates.main_y : sticky.main_y,
            main_width: updates.main_width !== undefined ? updates.main_width : sticky.main_width,
            main_height: updates.main_height !== undefined ? updates.main_height : sticky.main_height,
            
            // Right panel properties
            x: updates.x !== undefined ? updates.x : sticky.x,
            y: updates.y !== undefined ? updates.y : sticky.y,
            width: updates.width !== undefined ? updates.width : sticky.width,
            height: updates.height !== undefined ? updates.height : sticky.height,
            
            // Common properties
            // Common properties
            content: updates.content !== undefined ? updates.content : sticky.content,
            title: updates.title !== undefined ? updates.title : sticky.title,
            zIndex: updates.zIndex !== undefined ? updates.zIndex : sticky.zIndex,
            is_minimized: updates.is_minimized !== undefined ? updates.is_minimized : sticky.is_minimized,
            display_mode: updates.display_mode !== undefined ? updates.display_mode : sticky.display_mode,
            tag1: updates.tag1 !== undefined ? updates.tag1 : sticky.tag1,  // ADD THIS
            tag2: updates.tag2 !== undefined ? updates.tag2 : sticky.tag2,  // ADD THIS  
            type: sticky.type
          };
  
          // Only trigger API update for non-layout changes or the appropriate panel
          if (!isMainPanelUpdate || hasContentProps) {
           
            debouncedStickyContent(updatedSticky);
          }
  
          return updatedSticky;
        });
  
        return updatedStickies;
      });
    } catch (error) {
      console.error('Error updating sticky:', error);
      setError('Failed to update sticky');
    }
  }, [effectiveNoteId, debouncedStickyContent, isSwapped]);

  // Remove sticky - always from the effective note ID
  const removeSticky = useCallback(async (id) => {
    if (!effectiveNoteId) {
      console.warn('Attempted to remove sticky without valid noteId');
      return;
    }
  
    try {
      // Optimistic update
      setStickies(prevStickies => prevStickies.filter(sticky => sticky.id !== id));
      await stickiesApi.deleteSticky(effectiveNoteId, id);
    } catch (error) {
      console.error('Error removing sticky:', error);
      // Rollback on error
      fetchStickies(true); // Force reload on error
      setError('Failed to remove sticky');
    }
  }, [effectiveNoteId, fetchStickies]);

  // Save sticky layout - always on the effective note ID
  const saveStickyLayout = useCallback(async (layoutUpdates) => {
    try {
      await stickiesApi.updateStickyLayouts(effectiveNoteId, layoutUpdates);
    } catch (error) {
      console.error('Error saving layout:', error);
      setError('Failed to save layout');
    }
  }, [effectiveNoteId]);

  // Reorder stickies function
// Replace in StickiesContext.js - this will work immediately
const reorderStickies = useCallback(async (orderUpdates) => {
  if (!effectiveNoteId) return;
  
  console.log('🔍 REORDER DEBUG - orderUpdates received:', orderUpdates);
  console.log('🔍 REORDER DEBUG - effectiveNoteId:', effectiveNoteId);
  
  try {
    // Update local state immediately for responsive UI
    setStickies(prevStickies => {
      return prevStickies.map(sticky => {
        const update = orderUpdates.find(u => u.id === sticky.id);
        return update ? { ...sticky, list_order: update.list_order } : sticky;
      });
    });

    // Debug each individual update
    for (const update of orderUpdates) {
      console.log('🔍 REORDER DEBUG - About to update sticky:', update.id, 'with list_order:', update.list_order);
      
      const updatePayload = {
        list_order: update.list_order
      };
      
      console.log('🔍 REORDER DEBUG - Update payload:', updatePayload);
      
      // Check if the API function is receiving the data correctly
      const result = await stickiesApi.updateSticky(effectiveNoteId, update.id, updatePayload);
      console.log('🔍 REORDER DEBUG - API response:', result);
    }
    
    console.log('✅ Successfully reordered stickies using individual updates');
  } catch (error) {
    console.error('❌ Error reordering stickies:', error);
    setError('Failed to reorder stickies');
    // Rollback on error
    fetchStickies(true);
  }
}, [effectiveNoteId, fetchStickies]);

  // Load stickies whenever the effective note ID changes
  useEffect(() => {
    if (effectiveNoteId) {
      fetchStickies();
    }
  }, [effectiveNoteId, fetchStickies]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedStickyContent.cancel();
    };
  }, [debouncedStickyContent]);

  // Context value object with all the necessary data and functions
  const value = {
    stickies,
    loading,
    error,
    fetchStickies,
    addSticky,
    updateSticky,
    removeSticky,
    saveStickyLayout,
    reorderStickies, // Add this line
    effectiveNoteId, // Expose this so components can know which note ID is actually being used
    isSubnote: Boolean(parentNoteId) // Flag to indicate if we're viewing a subnote's parent stickies
  };

  return (
    <StickiesContext.Provider value={value}>
      {children}
    </StickiesContext.Provider>
  );
}

export function useStickies() {
  const context = useContext(StickiesContext);
  if (!context) {
    throw new Error('useStickies must be used within a StickiesProvider');
  }
  return context;
}